import React, { useEffect, useState } from 'react';
import api from '../api';

/**
 * BADGE_META — mirror of BADGES dict in backend/trader_stats.py.
 * If backend adds new badges, add them here too or extend the endpoint
 * to return the meta. Kept client-side to avoid a second network hop.
 */
export const BADGE_META = {
  first_blood:    { emoji: '🩸', label: 'First Blood',    description: 'The very first buyer of a Clan — you spotted the opportunity before anyone else.' },
  early_believer: { emoji: '🚀', label: 'Early Believer', description: 'One of the first 10 buyers of a Clan.' },
  diamond_hands:  { emoji: '💎', label: 'Diamond Hands',  description: 'Held Bits of a Clan for 7+ days without selling.' },
  whale_tamer:    { emoji: '🐋', label: 'Whale Tamer',    description: 'Own the maximum Bits allowed in a Clan.' },
  streaker:       { emoji: '🔥', label: 'Streaker',       description: 'Traded on 7 consecutive days.' },
  top_10:         { emoji: '👑', label: 'Top 10',         description: 'Finished a week in the top 10 of the weekly PnL leaderboard.' },
  deep_pockets:   { emoji: '🎯', label: 'Deep Pockets',   description: 'Traded 100,000+ Points of cumulative volume.' },
  sold_out:       { emoji: '🏆', label: 'Fully Minted',   description: "Every Bit of your Clan was claimed by the community — Creator's Bit cashed." },
};

/** Sort order — highest-prestige first (used both for full strip and micro chips).
 *  Note: `sold_out` intentionally omitted — legacy "Fully Minted" badge from
 *  the old linear-cap curve; sqrt V2 has no cap, so any residual awards on
 *  legacy clans are silently hidden here rather than mis-representing them
 *  on modern profiles. */
const BADGE_ORDER = ['top_10', 'whale_tamer', 'deep_pockets', 'first_blood', 'diamond_hands', 'early_believer', 'streaker'];

/** Badge keys we no longer surface anywhere in the UI (legacy/deprecated). */
const HIDDEN_BADGES = new Set(['sold_out']);

function sortBadges(keys) {
  const seen = new Set(keys);
  return BADGE_ORDER.filter((k) => seen.has(k));
}

/**
 * Full badge strip for a user's profile.
 * Fetches /api/badges/user/:handle on mount. Renders as pill chips.
 * Renders nothing if the user has zero badges.
 */
export function BadgeStrip({ handle }) {
  const [badges, setBadges] = useState(null);

  useEffect(() => {
    if (!handle) return;
    let cancelled = false;
    api.get(`/badges/user/${encodeURIComponent(handle)}`)
      .then(({ data }) => { if (!cancelled) setBadges(Array.isArray(data.badges) ? data.badges : []); })
      .catch(() => { if (!cancelled) setBadges([]); });
    return () => { cancelled = true; };
  }, [handle]);

  if (badges === null) return null;      // loading (silent)
  const visible = badges.filter((b) => !HIDDEN_BADGES.has(b.key));
  if (visible.length === 0) return null;  // no visible badges — hide the whole strip

  return (
    <div className="badge-strip" data-testid="badge-strip">
      {visible.map((b) => {
        const meta = BADGE_META[b.key] || { emoji: '🏅', label: b.key, description: '' };
        const clan = b.metadata?.clan;
        return (
          <span
            key={b.key + (b.awarded_at || '')}
            className="badge-chip"
            data-testid={`badge-chip-${b.key}`}
            title={
              clan
                ? `${meta.label} — ${meta.description}\nEarned via @${clan}`
                : `${meta.label} — ${meta.description}`
            }
          >
            <span className="badge-chip-emoji" aria-hidden="true">{meta.emoji}</span>
            <span className="badge-chip-label">{meta.label}</span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * Compact inline badges for use next to a user's name in Clan feed/chat.
 * Renders at most `max` badges as emoji-only chips. Accepts a list of
 * badge keys (already fetched via /api/badges/bulk by the parent).
 */
export function MicroBadges({ badgeKeys, max = 3 }) {
  if (!Array.isArray(badgeKeys) || badgeKeys.length === 0) return null;
  const filtered = badgeKeys.filter((k) => !HIDDEN_BADGES.has(k));
  const ordered = sortBadges(filtered).slice(0, max);
  return (
    <span className="micro-badges" data-testid="micro-badges" aria-hidden="true">
      {ordered.map((k) => {
        const meta = BADGE_META[k] || { emoji: '🏅', label: k };
        return (
          <span key={k} className="micro-badge" title={meta.label}>
            {meta.emoji}
          </span>
        );
      })}
    </span>
  );
}

export default BadgeStrip;
